import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { updateDoc, doc } from 'firebase/firestore';
import { db } from '../services/firebase';

const ThemeContext = createContext();

export function useTheme() {
  return useContext(ThemeContext);
}

export function ThemeProvider({ children }) {
  const { currentUser, userProfile } = useAuth();
  const [theme, setTheme] = useState('light');
  const [mounted, setMounted] = useState(false);

  // Initialize theme from user profile or localStorage
  useEffect(() => {
    if (userProfile?.themeMode) {
      setTheme(userProfile.themeMode);
    } else {
      // Fallback to localStorage for immediate feedback
      const savedTheme = localStorage.getItem('theme') || 'light';
      setTheme(savedTheme);
    }
    setMounted(true);
  }, [userProfile]);

  // Apply theme to document
  useEffect(() => {
    if (!mounted) return;

    const root = document.documentElement;
    root.setAttribute('data-theme', theme);
  }, [theme, mounted]);

  const toggleTheme = async () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);

    // Save to localStorage for immediate persistence
    localStorage.setItem('theme', newTheme);

    // Save to Firestore if user is logged in
    if (currentUser) {
      try {
        await updateDoc(doc(db, 'users', currentUser.uid), {
          themeMode: newTheme
        });
      } catch (error) {
        console.error('Error updating theme preference:', error);
      }
    }
  };

  const setThemeMode = async (newTheme) => {
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);

    if (currentUser) {
      try {
        await updateDoc(doc(db, 'users', currentUser.uid), {
          themeMode: newTheme
        });
      } catch (error) {
        console.error('Error updating theme preference:', error);
      }
    }
  };

  const value = {
    theme,
    toggleTheme,
    setTheme: setThemeMode,
    isDark: theme === 'dark'
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}